import useValidationSchema from "hooks/useValidationSchema";
import { Form, Field } from "react-final-form";
import { SlotsDetailFilterProps } from "./types";
import {
  AutoComplete,
  Button,
  DatePicker,
  MultiSelectInput,
  SelectInput,
} from "@pickit/pickit-components";
import { DATE_FORMATS } from "utils/constants";
import { SlotsFilterType } from "reducers/types/slots";
import { validationSchema } from "./schemas";
import "./i18n";
import { useTranslation } from "react-i18next";
import FilterPanel from "component/filterPanel/FilterPanel";
import { FILTER_PANEL_FORM_CLASS } from "component/filterPanel/types";

const SlotsDetailFilter = ({
  id,
  regions,
  workzones,
  status,
  filters,
  fetchingWorkzones,
  shifts,
  testId,
  onSubmit,
  onUpdateWorkzones,
  resetWorkzones,
  setActualRegion,
  setCollapseTable,
}: SlotsDetailFilterProps) => {
  const { t } = useTranslation();
  return (
    <FilterPanel
      layout="slots"
      title={t("global:filter")}
      testId={testId}
      collapseTable={setCollapseTable}
    >
      <div className="container-fluid">
        <div className="container-row">
          <div className="container-col" id={id}>
            <Form
              keepDirtyOnReinitialize
              onSubmit={(values: SlotsFilterType) => onSubmit(values)}
              initialValues={filters}
              mutators={{
                resetWorkzones: ([field, value], state, { changeValue }) => {
                  changeValue(state, "workzone", () => "");

                  if (!value || !value.id) {
                    resetWorkzones();
                    setActualRegion("");
                  }
                },
              }}
              validate={useValidationSchema(validationSchema)}
            >
              {({ handleSubmit, form, values, invalid }) => (
                <form
                  className={FILTER_PANEL_FORM_CLASS}
                  onSubmit={handleSubmit}
                >
                  <div className="container-col-sm-1">
                    <Field
                      type="text"
                      id={id + "-region"}
                      name="region"
                      onChange={form.mutators.resetWorkzones}
                      className="Admin-Pickers-input"
                      label={t("filterSlots:regionLabel")}
                      placeholder={t("filterSlots:regionPlaceholder")}
                      options={regions}
                      testId={`${testId}-region`}
                    >
                      {(props: any) => <AutoComplete {...props} />}
                    </Field>
                  </div>
                  <div className="container-col-sm-1">
                    <Field
                      type="text"
                      id={id + "-workzone"}
                      name="workzone"
                      className="Admin-Pickers-input"
                      label={t("filterTemplates:workzoneLabel")}
                      placeholder={t("filterTemplates:workzonePlaceholder")}
                      disabled={workzones?.length === 0 && !values.region}
                      testId={`${testId}-workzone`}
                      options={workzones}
                    >
                      {(props: any) => (
                        <MultiSelectInput
                          {...props}
                          labelAll={t("global:allF")}
                          isLoading={fetchingWorkzones}
                          onFocus={() =>
                            onUpdateWorkzones(values.region || "")
                          }
                        />
                      )}
                    </Field>
                  </div>
                  <div className="container-col-sm-1">
                    <Field
                      type="text"
                      id={id + "-date"}
                      name="date"
                      className="Admin-Pickers-input-select"
                      label={t("filterSlots:dateLabel")}
                      placeholder={t("filterSlots:datePlaceholder")}
                      language="es"
                      outputFormat="DD/MM/YY"
                      testId={`${testId}-date`}
                    >
                      {(props: any) => (
                        <DatePicker
                          outputFormat={DATE_FORMATS.shortDate}
                          {...props}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="container-col-sm-1">
                    <Field
                      type="text"
                      id={id + "-status"}
                      name="status"
                      label={t("filterSlots:statusLabel")}
                      placeholder={t("filterSlots:statusPlaceholder")}
                      className="Admin-Pickers-input"
                      options={status}
                      showProperty="description"
                      testId={`${testId}-status`}
                    >
                      {(props: any) => (
                        <MultiSelectInput keyProperty="tag" {...props} />
                      )}
                    </Field>
                  </div>
                  <div className="container-col-sm-1">
                    <Field
                      type="text"
                      id={id + "-shift"}
                      name="shift"
                      className="Admin-Pickers-input"
                      label={t("filterSlots:shiftLabel")}
                      placeholder={t("filterSlots:shiftPlaceholder")}
                      options={shifts}
                      testId={`${testId}-shift`}
                    >
                      {(props: any) => (
                        <SelectInput
                          showProperty="description"
                          keyProperty="tag"
                          {...props}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="container-col-sm-1" />
                  <div className="container-col-sm-1" />
                  <div className="end container-col-sm-1">
                    <Button
                      type="submit"
                      label={t("global:search")}
                      iconName="search"
                      testId={`${testId}-search-button`}
                      disabled={invalid}
                      size="large"
                      hierarchy="secondary"
                    />
                  </div>
                </form>
              )}
            </Form>
          </div>
        </div>
      </div>
    </FilterPanel>
  );
};

export default SlotsDetailFilter;
